import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { getAuthHeaders } from "../../../../utils/auth";

export const useCostData = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Data states
  const [trainers, setTrainers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [businessSettings, setBusinessSettings] = useState(null);
  const [lessonCategories, setLessonCategories] = useState([]);
  const [constantCosts, setConstantCosts] = useState([]);
  const [constantCostsSummary, setConstantCostsSummary] = useState(null);
  const [taxRateValue, setTaxRateValue] = useState("");

  // Include inactive toggle
  const [showInactiveConstantCosts, setShowInactiveConstantCosts] =
    useState(false);

  const clearMessages = useCallback(() => {
    setError("");
    setSuccess("");
  }, []);

  const showSuccessMessage = useCallback((message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(""), 3000);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const headers = await getAuthHeaders();

      const [
        trainersRes,
        locationsRes,
        settingsRes,
        categoriesRes,
        constantCostsRes,
        summaryRes,
      ] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/trainers`,
          { headers }
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/locations`,
          { headers }
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/settings`,
          { headers }
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/lesson-categories`,
          { headers }
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/constant-costs?include_inactive=${showInactiveConstantCosts}`,
          { headers }
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/constant-costs/summary`,
          { headers }
        ),
      ]);

      setTrainers(trainersRes.data);
      setLocations(locationsRes.data);
      setBusinessSettings(settingsRes.data);
      setTaxRateValue(settingsRes.data?.income_tax_rate?.toString() || "12");
      setLessonCategories(categoriesRes.data);
      setConstantCosts(constantCostsRes.data);
      setConstantCostsSummary(summaryRes.data);
    } catch (err) {
      console.error("Failed to fetch data", err);
      setError("Nie udało się załadować danych konfiguracji kosztów");
    } finally {
      setLoading(false);
    }
  }, [showInactiveConstantCosts]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get categories that are not yet assigned to a trainer
  const getAvailableCategories = useCallback(
    (trainerId) => {
      const trainer = trainers.find((t) => t.id === trainerId);
      const assignedCategoryIds = (trainer?.category_rates || []).map(
        (cr) => cr.lesson_category_id
      );
      return lessonCategories.filter(
        (cat) => !assignedCategoryIds.includes(cat.id)
      );
    },
    [trainers, lessonCategories]
  );

  return {
    // Loading and messages
    loading,
    error,
    success,
    setError,
    setSuccess,
    clearMessages,
    showSuccessMessage,

    // Data
    trainers,
    setTrainers,
    locations,
    setLocations,
    businessSettings,
    setBusinessSettings,
    lessonCategories,
    constantCosts,
    constantCostsSummary,
    taxRateValue,
    setTaxRateValue,

    // Toggle states
    showInactiveConstantCosts,
    setShowInactiveConstantCosts,

    // Actions
    fetchData,
    getAvailableCategories,
  };
};

export default useCostData;
